import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import MainHeader from "./presentation/screens/header/MainHeader";
import { Outlet, useNavigate } from "react-router";
import { NotFoundPage } from "./presentation/screens/not-found/NotFoundPage";
import MainFooter from "./presentation/screens/footer/MainFooter";
import { useStyleRepository } from "./data/repositories/StyleRepository";
import { useDesignRepository } from "./data/repositories/DesignRepository";
import { useLogoRepository } from "./data/repositories/LogoRepository";
import { useProfileRepository } from "./data/repositories/ProfileRepository";
import { useOrderRepository } from "./data/repositories/OrderRepository";
import { useManufacturerRepository } from "./data/repositories/ManufacturerRepository";
import { useUserRepository } from "./data/repositories/UserRepository";
import { useWhiteLabelContext, WhiteLabelProvider } from "./WhiteLabelContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import {
  RepositoriesProvider,
  useRepositoriesContext,
} from "./contexts/RepositoriesContext";
import LoadingElement from "./presentation/components/loading-element/loading-element";
import { useOrganizationRepository } from "./data/repositories/OrganizationRepository";
import { CartProvider } from "./contexts/CartContext";
import { useCountryRepository } from "./data/repositories/CountryRepository";
import { useCbsConfigRepository } from "./data/repositories/CbsConfigRepository";
import { useStatisticsRepository } from "./data/repositories/StatisticsRepository";
import { useApiKeyRepository } from "./data/repositories/ApiKeyRepository";
import { usePlaceOrderQueueRepository } from "./data/repositories/PlaceOrderQueueRepository";
import { useTransferMethodConfigRepository } from "./data/repositories/TransferMethodConfigRepository";
import { useStockOrdersConfigRepository } from "./data/repositories/StockOrdersConfigRepository";
import { useDesignFolderRepository } from "./data/repositories/DesignFolderRepository";
import { useContentRepository } from "./data/repositories/ContentRepository";
import { usePrepaymentsRepository } from "./data/repositories/PrepaymentsRepository";
import { useImportFailuresRepository } from "./data/repositories/ImportFailuresRepository";
import { useSystemHealthRepository } from "./data/repositories/SystemHealthRepository";
import { useStripePayoutsRepository } from "./data/repositories/StripePayoutsRepository";
import { useNavPriceFallbackRepository } from "./data/repositories/NavPriceFallbackRepository";
import { useMailTestRepository } from "./data/repositories/MailTestRepository";
import { useApprovalMailRepository } from "./data/repositories/ApprovalMailRepository";
import { CountryProvider } from "./presentation/screens/design-editor2/contexts/country.context";
import { PageQuery } from "./utils/models/pageQuery";
import { InteractionStatus } from "@azure/msal-browser";
import { JUST_LOGGED_OUT_KEY, loginRequest } from "./AuthConfig";
import { OrganizationImpersionationProvider } from "./contexts/OrganizationImpersonationContext";
import { LogoProvider } from "./presentation/screens/design-editor2/contexts/logo-options.context";
import { PriceVisibilityProvider } from "./contexts/PriceVisibilityContext";
import { getInitialQuery } from "./presentation/shared/functions/getInitialQuery.function";
import { getAllQuery } from "./presentation/screens/logo-editor/logo-editor.screen";
import {
  IMPERSONATION_BANNER_HEIGHT_REM,
  useImpersonationBannerVisible,
} from "./presentation/screens/header/subcomponents/ImpersonationBanner";
import { ImpersonationFrame } from "./presentation/screens/header/subcomponents/ImpersonationFrame";
import { SupplierStatusBanner } from "./presentation/components/SupplierStatusBanner/SupplierStatusBanner";
import { ClarityIdentity } from "./presentation/components/Clarity/ClarityIdentity";
import { ConsentBanner } from "./presentation/components/ConsentBanner/ConsentBanner";

// The fixed header is really nav info-strip (h-7) + main nav (h-16) + border
// ≈ 93px; 6.75rem (108px) has always been the hard-coded reservation for it.
// While the act-as banner renders inside the fixed header it adds another
// 2.25rem, which must be reserved too — otherwise the banner overlaps the top
// of every screen (the yellow-strip bug).
function useHeaderPaddingTop(): string {
  const bannerVisible = useImpersonationBannerVisible();
  return bannerVisible
    ? `calc(6.75rem + ${IMPERSONATION_BANNER_HEIGHT_REM}rem)`
    : "6.75rem";
}

interface Props {
  notfound?: boolean;
}

interface ScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  style?: React.CSSProperties & {
    [key: `--${string}`]: string | number;
  };
}

const Screen = React.forwardRef<HTMLDivElement, ScreenProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        {...rest}
        className={
          "app w-screen min-h-screen h-screen overflow-auto " +
          (className ?? "")
        }
      >
        {children}
      </div>
    );
  },
);

export function App({ notfound = false }: Props): React.ReactElement {
  function MainView() {
    function AuthenticatedView() {
      const { whiteLabel } = useWhiteLabelContext();
      const paddingTop = useHeaderPaddingTop();
      return (
        <Screen
          style={{
            "--primary": whiteLabel.colorHex,
            "--primary-bg": `${whiteLabel.colorHex}10`,
          }}
        >
          {/* No height on the wrapper: the header (info-strip + nav + banner)
              is taller than the h-16 that used to sit here and simply
              overflowed it — the class controlled nothing and misled. */}
          <div className={"w-full fixed z-20 top-0"}>
            <MainHeader />
          </div>
          <div className={"relative"}>
            <div
              style={{
                minHeight: "100vh",
                paddingTop,
                paddingBottom: "5rem",
                marginBottom: "-4.05rem",
              }}
              className={"w-full table  h-full bg-background"}
            >
              <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
                {notfound ? <NotFoundPage /> : <Outlet />}
              </div>
            </div>
            <div className={"w-full h-16 absolute z-20 bottom-[1px]"}>
              <MainFooter />
            </div>
          </div>
        </Screen>
      );
    }

    return (
      <>
        <AuthenticatedTemplate>
          <PreLoader>
            <AuthenticatedView />
          </PreLoader>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <UnauthenticatedView />
        </UnauthenticatedTemplate>
      </>
    );
  }

  return <MainView />;
}

export function PublicView() {
  return (
    <Screen style={{ "--primary": "#7BBB58", "--primary-bg": `#7BBB5810` }}>
      <div className={"relative"}>
        <div
          style={{
            minHeight: "100vh",
            paddingTop: "4rem",
            paddingBottom: "5rem",
            marginBottom: "-4.05rem",
          }}
          className={"w-full table h-full bg-background"}
        >
          <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
            {false ? <NotFoundPage /> : <Outlet />}
          </div>
        </div>
      </div>
    </Screen>
  );
}

function AuthenticatedView() {
  const { whiteLabel } = useWhiteLabelContext();
  const paddingTop = useHeaderPaddingTop();

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    const baseUrl =
      "https://stkitmakerdev.blob.core.windows.net/b2c-login-page/";
    let title = "KitMaker";
    let favicon = "/favicon.ico";

    if (hostname.includes("uk.jetsport.dk")) {
      title = "ID Unlimited - Webshop";
      favicon = baseUrl + "favicon-id-unlimited.ico";
    } else if (hostname.includes("es.jetsport.dk")) {
      title = "ID Unlimited - Webshop";
      favicon = baseUrl + "favicon-id-unlimited.ico";
    } else if (hostname.includes("js.jetsport.dk")) {
      title = "Jet Sport - Webshop";
      favicon = baseUrl + "favicon-jetsport.ico";
    } else if (hostname.includes("ns.jetsport.dk")) {
      title = "NS System - Webshop";
      favicon = baseUrl + "favicon-nssystem.ico";
    } else if (hostname.includes("dos4u.jetsport.dk")) {
      title = "DOS4U - Webshop";
      favicon = baseUrl + "favicon-dos4u.ico";
    }

    document.title = title;

    let link = document.querySelector(
      "link[rel*='icon']",
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "shortcut icon";
      document.head.appendChild(link);
    }
    link.type = "image/x-icon";
    link.href = favicon;
  }, []);

  return (
    <Screen
      style={{
        "--primary": whiteLabel.colorHex,
        "--primary-bg": `${whiteLabel.colorHex}10`,
      }}
    >
      {/* See the twin AuthenticatedView above: no height on the wrapper. */}
      <div className={"w-full fixed z-20 top-0"}>
        <MainHeader />
      </div>
      {/* Sibling of (not inside) the z-20 header wrapper: the frame needs its
          own top-level stacking context to sit above dialogs/overlays. NB this
          is the LIVE AuthenticatedView (PrivateView renders it) — the twin
          inside the unused App() above is dead code. */}
      <ImpersonationFrame />
      <div className={"relative"}>
        <div
          style={{
            minHeight: "100vh",
            paddingTop,
            paddingBottom: "5rem",
            marginBottom: "-4.05rem",
          }}
          className={"w-full table  h-full bg-background"}
        >
          <div className="max-w-[90rem] w-full max-h-full h-full mx-auto">
            {/* Flag REACT_APP_SUPPLIER_STATUS_ENABLED: the amber "reduced
                functionality" bar while the logo supplier is degraded, in
                flow above the routed screen (never over it). Mounted here
                - after the profile (PreLoader) and inside the router - so
                its ONE status query lives for the whole session. Flag off
                = a null component: byte-identical DOM, no hook, no call. */}
            {/* Flags REACT_APP_CLARITY_ENABLED / REACT_APP_CLARITY_CONSENT_
                ENABLED: null component that sends the Clarity identity, and
                the consent banner (only here - never on PublicView or
                /approval). Both are null components with the flags off. */}
            <ClarityIdentity />
            <ConsentBanner />
            <SupplierStatusBanner />
            {false ? <NotFoundPage /> : <Outlet />}
          </div>
        </div>
        <div className={"w-full h-16 absolute z-20 bottom-[1px]"}>
          <MainFooter />
        </div>
      </div>
    </Screen>
  );
}

// Ét fælles splash-udtryk for HELE boot-forløbet. Screen'en får appens egen
// baggrund (bg-background #FBFCFF - samme som index.html's statiske #km-splash
// og den færdige app), og LoadingElements grå bg-loading overstyres
// tilsvarende, så splash→app aldrig klipper i farve - kun indholdet kommer
// til. Bruges af alle tre boot-tilstande (BootSplash, UnauthenticatedView,
// PreLoader), så remounts imellem dem er pixel-identiske; LoadingElement
// skipper selv fade-in efter første mount, så skiftene er usynlige.
function BootScreen() {
  return (
    <Screen className="bg-background">
      <LoadingElement className="!bg-background" />
    </Screen>
  );
}

// During Startup/HandleRedirect neither template renders, which used to leave
// a blank white screen on every auth round-trip (login, logout-return, token
// redirect) - the "blinking". Show the same splash instead so the whole
// journey is one steady loading state.
function BootSplash() {
  const { inProgress } = useMsal();
  if (
    inProgress !== InteractionStatus.Startup &&
    inProgress !== InteractionStatus.HandleRedirect &&
    // Logout var det sidste udækkede hul: mens logoutRedirect afvikles,
    // renderer ingen af MSAL-skabelonerne, og skærmen stod BLANK indtil
    // B2C-rundturen navigerede væk - "log ud-blinket". Samme splash dækker
    // nu hele vejen ud, præcis som den dækker vejen ind.
    inProgress !== InteractionStatus.Logout
  ) {
    return null;
  }
  return <BootScreen />;
}

export function PrivateView() {
  return (
    <>
      <BootSplash />
      <AuthenticatedTemplate>
        <PreLoader>
          <AuthenticatedView />
        </PreLoader>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <UnauthenticatedView />
      </UnauthenticatedTemplate>
    </>
  );
}

// Single-flight guard for the auto-started login: exactly ONE loginRedirect
// per page load (same pattern as AuthorizedFetch's tokenRedirectStarted).
// Module scope on purpose - a remount of UnauthenticatedView must not grant a
// fresh attempt; only a full page load (which is how every B2C round-trip
// returns) resets it.
let autoLoginStarted = false;

// Safari (og i stigende grad Chrome) genopliver sider fra back-forward-cachen
// UDEN nyt sideload: trykker brugeren Tilbage fra B2C-login-siden, vågner
// appen frosset midt i auto-login-forløbet - vagten er sat, effekten
// genkøres ikke, og brugeren ville stå fast på splashen. pageshow med
// persisted=true er standard-krogen for præcis dét: ét friskt sideload
// giver en ren boot, hvor auto-login flyder normalt. Betingelsen på vagten
// gør reloaden kirurgisk - almindelige app-sider (logget ind, browsing)
// sætter aldrig vagten og genoplives urørt fra cachen.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted && autoLoginStarted) {
      autoLoginStarted = false;
      window.location.reload();
    }
  });
}

function UnauthenticatedView() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // A deliberate logout (marker set in useNavigationBarViewModel.logout) must
  // NOT auto-start a new login - Safari's surviving B2C SSO cookie made that
  // bounce users straight back in. The marker lives in localStorage because
  // Safari drops sessionStorage across the cross-site logout round-trip.
  // postLogoutRedirectUri is '/' so the post-logout landing is always '/'; on
  // any other path (deep link later) we drop the marker and keep the normal
  // login-required flow.
  const logoutMarker = localStorage.getItem(JUST_LOGGED_OUT_KEY);
  const justLoggedOut = logoutMarker !== null;
  // ALDERS-VÆRN (målt nødvendigt 14/8): markøren bærer et tidsstempel, og en
  // FRISK markør betyder "logout er i luften lige nu eller landede for et
  // øjeblik siden". Uanset hvordan MSAL's tilstande interleaver under
  // teardown (der FINDES et hvile-vindue, hvor inProgress-vagten alene ikke
  // rækker - auto-login nåede at vinde kapløbet mod logout-navigationen og
  // sendte brugeren til /authorize i stedet for /logout), må en frisk markør
  // hverken ryddes eller udløse auto-login. Gamle markører (inkl. "1" fra
  // før formatskiftet, som parser til alder uendelig) opfører sig som hidtil.
  const logoutMarkerAgeMs = logoutMarker
    ? Date.now() - (Number.parseInt(logoutMarker, 10) || 0)
    : Number.POSITIVE_INFINITY;
  const logoutInFlight = justLoggedOut && logoutMarkerAgeMs < 10_000;
  const showSignedOutScreen =
    justLoggedOut && window.location.pathname === "/";

  // Auto-login as an EFFECT with a single-flight guard - not a render side
  // effect. The old render-time call could fire twice in quick succession
  // (two renders before the redirect commits): call #2 overwrote the PKCE
  // state/nonce that call #1's B2C round-trip needs, so the returning code
  // was dropped -> no account -> a THIRD loginRedirect... a self-sustaining
  // restart loop the user experienced as the app "blinking" between the
  // login page and the splash (observed live 13/8-2026). One attempt per
  // page load; B2C's round-trip always lands in a fresh page load, so the
  // guard can never block a legitimate retry - and the catch re-arms it if
  // the redirect fails to start at all. (The effect must sit BEFORE the
  // signed-out early return - rules of hooks.)
  const shouldAutoLogin =
    !showSignedOutScreen &&
    !logoutInFlight &&
    !isAuthenticated &&
    inProgress === InteractionStatus.None;
  useEffect(() => {
    if (!shouldAutoLogin || autoLoginStarted) return;
    autoLoginStarted = true;
    sessionStorage.setItem(
      "redirectOnLogin",
      window.location.pathname + window.location.search,
    );
    instance.loginRedirect(loginRequest).catch(() => {
      autoLoginStarted = false;
    });
  }, [shouldAutoLogin, instance]);

  // Ryd KUN en forældet markør i hvile (inProgress === None). MSAL's
  // UnauthenticatedTemplate udelukker ikke Logout-tilstanden (verificeret i
  // den installerede kilde), så denne komponent renderer OGSÅ midt i selve
  // logout-forløbet - stadig på fx /logo, hvor pathname-tjekket fejlagtigt
  // lignede et "forældet dyb-link" og slettede markøren, brugeren lige havde
  // fået sat. Konsekvens (filmet på prod 14/8): hjemkomsten fra B2C startede
  // et NYT login i stedet for at vise logget ud-skærmen - ekstra rundtur,
  // ekstra blink, forkert slutning. I hvile på en dyb-link-sti er rydningen
  // stadig korrekt (det oprindelige Safari-scenarie).
  if (
    justLoggedOut &&
    !logoutInFlight &&
    window.location.pathname !== "/" &&
    inProgress === InteractionStatus.None
  ) {
    localStorage.removeItem(JUST_LOGGED_OUT_KEY);
  } else if (justLoggedOut && showSignedOutScreen) {
    return <SignedOutScreen />;
  }

  return <BootScreen />;
}

// Logget ud-skærmen med nedtælling: efter 6 sekunder sendes brugeren selv
// videre til login-siden - samme handling som Sign in-knappen, inkl.
// prompt:'login' (en overlevende SSO-cookie må ikke logge brugeren lydløst
// ind igen efter et bevidst logout). Egen komponent, fordi nedtællingen
// kræver hooks, og skærmen renderes fra en betinget gren i
// UnauthenticatedView (hooks må ikke bo dér).
function SignedOutScreen() {
  const { instance, inProgress } = useMsal();
  const [secondsLeft, setSecondsLeft] = useState(6);
  const redirectStartedRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(
      () => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(timer);
  }, []);

  const goToSignIn = () => {
    if (redirectStartedRef.current) return;
    if (inProgress !== InteractionStatus.None) return;
    redirectStartedRef.current = true;
    // Clear the marker BEFORE redirecting: if the login round-trip fails,
    // the next render falls through to the normal auto-login instead of
    // stranding the user on this screen.
    localStorage.removeItem(JUST_LOGGED_OUT_KEY);
    instance.loginRedirect({ ...loginRequest, prompt: "login" }).catch(() => {
      redirectStartedRef.current = false;
    });
  };

  // Ved nul: afsted. inProgress er med i deps, så et hjemkomst-efterslæb i
  // MSAL (ikke-None ved nul) blot udskyder redirectet til første hvile.
  useEffect(() => {
    if (secondsLeft === 0) goToSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, inProgress]);

  return (
    // Samme baggrund som BootScreen/app'en: hjemkomsten fra B2C-logout går
    // km-splash -> BootScreen -> denne skærm uden farve-klip undervejs.
    <Screen className="bg-background">
      <div className="flex h-screen flex-col items-center justify-center gap-5">
        <p className="text-lg font-semibold text-gray-800">
          You have been signed out.
        </p>
        <button
          type="button"
          disabled={inProgress !== InteractionStatus.None}
          onClick={goToSignIn}
          className="rounded-md bg-gray-900 px-8 py-2.5 font-semibold text-white transition hover:opacity-85"
        >
          Sign in
        </button>
        <p className="text-sm text-gray-500">
          {secondsLeft > 0
            ? `Continuing to sign-in in ${secondsLeft}…`
            : "Continuing…"}
        </p>
      </div>
    </Screen>
  );
}

export function PreLoader(props: PropsWithChildren) {
  const styleRepository = useStyleRepository();
  const designRepository = useDesignRepository();
  const logoRepository = useLogoRepository();
  const organizationRepository = useOrganizationRepository();
  const orderRepository = useOrderRepository();
  const manufacturerRepository = useManufacturerRepository();
  const userRepository = useUserRepository();
  const countryRepository = useCountryRepository();
  const statisticsRepository = useStatisticsRepository();
  const cbsConfigRepository = useCbsConfigRepository();
  const apiKeyRepository = useApiKeyRepository();
  const placeOrderQueueRepository = usePlaceOrderQueueRepository();
  const transferMethodConfigRepository = useTransferMethodConfigRepository();
  const stockOrdersConfigRepository = useStockOrdersConfigRepository();
  const designFolderRepository = useDesignFolderRepository();
  const contentRepository = useContentRepository();
  const prepaymentsRepository = usePrepaymentsRepository();
  const importFailuresRepository = useImportFailuresRepository();
  const systemHealthRepository = useSystemHealthRepository();
  const stripePayoutsRepository = useStripePayoutsRepository();
  const navPriceFallbackRepository = useNavPriceFallbackRepository();
  const mailTestRepository = useMailTestRepository();
  const approvalMailRepository = useApprovalMailRepository();

  const repositories = {
    styleRepository,
    designRepository,
    logoRepository,
    organizationRepository,
    orderRepository,
    manufacturerRepository,
    userRepository,
    countryRepository,
    statisticsRepository,
    cbsConfigRepository,
    apiKeyRepository,
    placeOrderQueueRepository,
    transferMethodConfigRepository,
    stockOrdersConfigRepository,
    designFolderRepository,
    contentRepository,
    prepaymentsRepository,
    importFailuresRepository,
    systemHealthRepository,
    stripePayoutsRepository,
    navPriceFallbackRepository,
    mailTestRepository,
    approvalMailRepository,
  };

  const { useProfile } = useProfileRepository();
  const { isLoading, refetch } = useProfile();
  const { accounts } = useMsal();
  const id = accounts[0]?.homeAccountId;

  // Refetch only when the account actually changes. The old unconditional
  // mount refetch restarted the just-started profile call (React Query's
  // cancelRefetch defaults to true), doubling GetProfile on every login -
  // and login always arrives via a full B2C redirect (fresh cache), so the
  // first mount has nothing stale to refetch.
  const prevId = useRef(id);
  useEffect(() => {
    // The prev-value guard (not the deps array) decides whether to refetch,
    // so extra effect runs from changing function identities are harmless.
    if (prevId.current === id) return;
    prevId.current = id;
    refetch();
  }, [id, refetch]);

  // Backstop: a stale "just logged out" marker must never survive into an
  // authenticated session (e.g. a logout that set it but never completed).
  useEffect(() => {
    localStorage.removeItem(JUST_LOGGED_OUT_KEY);
  }, []);

  const redirectOnLogin = sessionStorage.getItem("redirectOnLogin");
  const navigate = useNavigate();

  useEffect(() => {
    if (!redirectOnLogin) return;
    sessionStorage.removeItem("redirectOnLogin");
    // Only navigate on the post-B2C boot landing: redirectUri is '/' so every
    // real auth round-trip lands there, and that '/' entry is dead - replace
    // it. A marker that survived WITHOUT a round-trip (AuthorizedFetch sets
    // it before acquireTokenRedirect, which can fail without navigating) must
    // not hijack - and with replace, destroy - whatever page the user is
    // actually on later; just clearing it is the correct outcome then.
    if (window.location.pathname !== "/") return;
    navigate(redirectOnLogin, { replace: true });
  }, [redirectOnLogin]);

  // Warm the destination screen's blocking queries in parallel with the
  // profile call happening above (behind the splash). Today those queries only
  // start once the profile has finished - a purely architectural
  // serialization. This is fail-silent cache warming with the exact keys the
  // screens use; on a miss the screens fetch exactly as before, so behaviour
  // is unchanged. The redirectOnLogin value captured on first render is used
  // because the effect above clears sessionStorage; window.location covers
  // authenticated reloads where no redirect marker exists.
  useEffect(() => {
    // Exact-match the destination: prefetching the default first page for a
    // deep link (e.g. /logo?pageIndex=3) would fire a heavy list request
    // whose cache key the screen would never read.
    // window.location covers authenticated reloads - but only without query
    // params (a reload on /logo?pageIndex=3 must not warm the default page-1
    // keys the screen never reads).
    const destination =
      redirectOnLogin ||
      (window.location.search === "" ? window.location.pathname : "");

    // logo-options + countries are fetched by PreloadData for EVERY
    // authenticated session, no matter the destination — warming them here is
    // strictly the same two requests started earlier (in parallel with the
    // profile call instead of after it). Same keys/queryFns as
    // useOptions/useAllCountries, fail-silent, so behaviour is unchanged.
    logoRepository.prefetchOptions();
    countryRepository.prefetchAllCountries({
      page: { index: 0, size: 0 },
    } as PageQuery);

    if (destination === "/" || destination === "/logo") {
      // The logo list gates on logos + options + customers + vendors.
      // getInitialQuery(new URLSearchParams()) and getAllQuery are exactly
      // what the screens compute, so the cache keys match by construction
      // (same precedent as the cross-prefetch).
      logoRepository.prefetchLogos(getInitialQuery(new URLSearchParams()));
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(getAllQuery);
    } else if (destination === "/design") {
      // Mirror wave for the design list's heaviest gates.
      designRepository.prefetchDesigns(getInitialQuery(new URLSearchParams()));
      styleRepository.prefetchStyles(getAllQuery);
      manufacturerRepository.prefetchManufacturers();
    } else if (destination === "/order") {
      // Mirror wave for the order list's gates: orders + customers + vendors
      // (useCustomerAndVendors). getInitialQuery(new URLSearchParams(), 25)
      // is exactly what the screen's usePageQuery(25) computes on a fresh
      // visit, so the ["orders", "?pageSize=25&pageIndex=1"] cache key
      // matches by construction. List pages only - GetOrder details are
      // never prefetched.
      orderRepository.prefetchOrders(getInitialQuery(new URLSearchParams(), 25));
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(getAllQuery);
    }
    // Mount-only by design (repository objects are recreated every render).
    // eslint-disable-next-line
  }, []);

  return !accounts[0] || isLoading ? (
    // Splash instead of a blank screen while the profile loads after login -
    // keeps the boot journey one continuous loading state.
    <BootScreen />
  ) : (
    <RepositoriesProvider repositories={repositories}>
      <OrganizationImpersionationProvider>
        <ProfileProvider>
          <WhiteLabelProvider>
            <CartProvider>
              <PriceVisibilityProvider>
                <PreloadData>{props.children}</PreloadData>
              </PriceVisibilityProvider>
            </CartProvider>
          </WhiteLabelProvider>
        </ProfileProvider>
      </OrganizationImpersionationProvider>
    </RepositoriesProvider>
  );
}

function PreloadData(props: PropsWithChildren) {
  const { logoRepository, countryRepository } = useRepositoriesContext();
  const { useAllCountries } = countryRepository;
  const { data } = logoRepository.useOptions();
  const { data: countries } = useAllCountries({
    page: { index: 0, size: 0 },
  } as PageQuery);

  // Non-blocking preload: render the app immediately and let options/countries load in
  // the background (they only feed the design editor's pickers and country dropdowns).
  // Previously this returned <></> until both resolved, holding every page's first paint
  // and delaying the page's own data fetching by ~0.5-1s. Providers follow their props,
  // so late-arriving data propagates to consumers.
  return (
    <CountryProvider countries={countries?.results.flatMap((x) => x) ?? []}>
      <LogoProvider options={data}>{props.children}</LogoProvider>
    </CountryProvider>
  );
}
